import { useMutation } from "@apollo/client";
import { Dispatch, SetStateAction, useCallback, useState } from "react";
import {
  DeleteAllWaitingListsDocument,
  DeleteAllWaitingListsMutation,
  DeleteAllWaitingListsMutationVariables,
  DeleteWaitingListsDocument,
  DeleteWaitingListsMutation,
  DeleteWaitingListsMutationVariables,
  EditWaitingListDocument,
  EditWaitingListMutation,
  EditWaitingListMutationVariables,
  EditWaitingListUserDocument,
  EditWaitingListUserMutation,
  EditWaitingListUserMutationVariables,
  ToggleWaitingListDocument,
  ToggleWaitingListMutation,
  ToggleWaitingListMutationVariables,
  WaitingList,
  WaitingListUser,
} from "../../../graphql/operations";

type UseWaitingListProps = Partial<WaitingListUser>;

export default function useWaitingList(props?: UseWaitingListProps) {
  const [timeZone, setTimeZone] = useState(props?.timeZone);
  const [waitingLists, setWaitingLists] = useState(
    sortWaitingLists(props?.waitingLists ?? [])
  );

  const editUserMutation = useMutation<
    EditWaitingListUserMutation,
    EditWaitingListUserMutationVariables
  >(EditWaitingListUserDocument)[0];

  const editWaitingListMutation = useMutation<
    EditWaitingListMutation,
    EditWaitingListMutationVariables
  >(EditWaitingListDocument)[0];

  const toggleWaitingListMutation = useMutation<
    ToggleWaitingListMutation,
    ToggleWaitingListMutationVariables
  >(ToggleWaitingListDocument)[0];

  const deleteWaitingListMutation = useMutation<
    DeleteWaitingListsMutation,
    DeleteWaitingListsMutationVariables
  >(DeleteWaitingListsDocument)[0];

  const deleteAllWaitingListsMutation = useMutation<
    DeleteAllWaitingListsMutation,
    DeleteAllWaitingListsMutationVariables
  >(DeleteAllWaitingListsDocument)[0];

  const changeTimeZone = useCallback(
    async (timeZone: string, persist: boolean = false, id?: string) => {
      setTimeZone(timeZone);
      if (persist)
        return await editUserMutation({
          variables: {
            input: {
              id: props.id ?? id,
              timeZone,
            },
          },
        });
    },
    [props?.id, editUserMutation]
  );

  const sortAndSetWaitingLists = useCallback(
    (
      args: WaitingList[] | ((waitingLists: WaitingList[]) => WaitingList[])
    ) => {
      setWaitingLists((waitingLists) =>
        sortWaitingLists(typeof args === "function" ? args(waitingLists) : args)
      );
    },
    [setWaitingLists]
  ) as Dispatch<SetStateAction<WaitingList[]>>;

  const addWaitingList = useCallback(
    async (
      waitingList: Omit<WaitingList, "id" | "contacted"> &
        Partial<Pick<WaitingList, "id">> &
        Partial<Pick<WaitingListUser, "email" | "phoneNumber" | "timeZone">>,
      timeZone?: WaitingListUser["timeZone"],
      persist: boolean = false
    ) => {
      if (!persist && "id" in waitingList)
        sortAndSetWaitingLists((waitingLists) => [
          ...waitingLists,
          waitingList as WaitingList,
        ]);

      if (persist) {
        const mutationResult = await editWaitingListMutation({
          variables: {
            input: {
              ...waitingList,
              timeZone,
            },
          },
        });

        if (typeof window.gtag === "function")
          window.gtag("event", "waiting_list_signup");

        sortAndSetWaitingLists((waitingLists) => [
          ...waitingLists,
          ...mutationResult.data.editWaitingList,
        ]);

        return mutationResult;
      }
    },
    [editWaitingListMutation, sortAndSetWaitingLists]
  );

  const deleteWaitingLists = useCallback(
    async (ids: string | string[], persist: boolean = false) => {
      const idsToRemove = typeof ids === "string" ? [ids] : ids;
      sortAndSetWaitingLists((waitingLists) =>
        waitingLists.filter(
          (waitingList) => !idsToRemove.includes(waitingList.id)
        )
      );
      if (persist)
        return await deleteWaitingListMutation({
          variables: {
            ids: idsToRemove,
          },
        });
    },
    [deleteWaitingListMutation, sortAndSetWaitingLists]
  );

  const deleteAllWaitingLists = useCallback(
    async (persist: boolean = false, id?: string) => {
      setWaitingLists([]);
      if (persist)
        return await deleteAllWaitingListsMutation({
          variables: {
            id: id ?? props.id,
          },
        });
    },
    [deleteAllWaitingListsMutation, props?.id]
  );
  const toggleWaitingList = async (id: string, persist: boolean = false) => {
    const index = waitingLists.findIndex(
      (waitingList) => waitingList.id === id
    );

    const toggledWaitingList = {
      ...waitingLists[index],
      active: !waitingLists[index].active,
    };

    setWaitingLists((waitingLists) => {
      waitingLists[index] = toggledWaitingList;
      return [...waitingLists];
    });

    if (!persist) return;

    const { __typename, ...waitingList } = toggledWaitingList;

    return await toggleWaitingListMutation({
      variables: {
        id: waitingList.id,
      },
    });
  };

  return {
    waitingLists,
    timeZone,
    setTimeZone: changeTimeZone,
    addWaitingList,
    deleteWaitingLists,
    deleteAllWaitingLists,
    toggleWaitingList,
    sortAndSetWaitingLists,
  };
}

const sortWaitingLists = (waitingLists: WaitingList[]) =>
  [...waitingLists].sort((a, b) => {
    const productComparison = a.productId.localeCompare(b.productId);
    if (productComparison) return productComparison;
    const groupComparison = a.groupSize - b.groupSize;
    if (groupComparison) return groupComparison;
    const dateComparison = a.fromDate.localeCompare(b.fromDate);
    if (dateComparison) return dateComparison;
    return 0;
  });
